import EventBooking from "../../stores/domainStore/pages/models/eventBooking";
import {SortOrder} from "../../shared/enums/sortOrder";
import {BookingColumnNames} from "../../shared/enums/eventBookingColumnNames";
import {compareNumbers, compareStrings} from "./sortUtil";

export class BookingsSortUtil {
  private readonly bookings: EventBooking[];
  private readonly sortField: BookingColumnNames;
  private readonly sortOrder: SortOrder;

  constructor(
    bookings: EventBooking[],
    sortColumn: BookingColumnNames,
    sortOrder: SortOrder
  ) {
    this.bookings = Array.from(bookings);
    this.sortField = sortColumn;
    this.sortOrder = sortOrder;
  }

  sort(): EventBooking[] {
    if (!this.bookings || this.bookings.length === 0) return [];

    if (this.sortField === BookingColumnNames.Notes) {
      return this.sortNotes();
    }

    return this.bookings.sort((a: EventBooking, b: EventBooking) => {
      if (this.sortField === BookingColumnNames.Index)
        return this.compareIndex(a, b);

      const aVal = this.getProperty(a);
      const bVal = this.getProperty(b);

      const diff = compareStrings(aVal, bVal, this.sortOrder);
      // Secondary sort on index
      return diff === 0 ? this.compareIndex(a, b) : diff;
    });
  }

  private compareIndex = (a: EventBooking, b: EventBooking) =>
    compareNumbers(a.index, b.index, this.sortOrder);

  private sortNotes(): EventBooking[] {
    return this.bookings.sort((a: EventBooking, b: EventBooking) => {
      const aHasResidentNote: boolean = !!a.notes?.residentNote;
      const aHasStaffNote: boolean = !!a.notes?.staffNote;
      const bHasResidentNote: boolean = !!b.notes?.residentNote;
      const bHasStaffNote: boolean = !!b.notes?.staffNote;

      if (this.sortOrder === SortOrder.Ascending) {
        // When sorting ascending, app notes come first
        if (aHasResidentNote && !bHasResidentNote) {
          return -1;
        }
        if (bHasResidentNote && !aHasResidentNote) {
          return 1;
        }
        if (aHasResidentNote && bHasResidentNote) {
          const diff = compareStrings(
            a.notes?.residentNote,
            b.notes?.residentNote,
            this.sortOrder
          );
          // Secondary sort on index
          return diff === 0 ? this.compareIndex(a, b) : diff;
        }

        // Neither booking have resident note, compare staff note
        if (aHasStaffNote && !bHasStaffNote) {
          return -1;
        }
        if (bHasStaffNote && !aHasStaffNote) {
          return 1;
        }
        if (aHasStaffNote && bHasStaffNote) {
          const diff = compareStrings(
            a.notes?.staffNote,
            b.notes?.staffNote,
            this.sortOrder
          );
          // Secondary sort on index
          return diff === 0 ? this.compareIndex(a, b) : diff;
        }

        // Neither have any note
        return this.compareIndex(a, b);
      } else {
        // When sorting descending, staff notes come first
        if (aHasStaffNote && !bHasStaffNote) {
          return -1;
        }
        if (bHasStaffNote && !aHasStaffNote) {
          return 1;
        }
        if (aHasStaffNote && bHasStaffNote) {
          const diff = compareStrings(
            a.notes?.staffNote,
            b.notes?.staffNote,
            this.sortOrder
          );
          // Secondary sort on index
          return diff === 0 ? this.compareIndex(a, b) : diff;
        }

        // Neither booking have staff note, compare resident note
        if (aHasResidentNote && !bHasResidentNote) {
          return -1;
        }
        if (bHasResidentNote && !aHasResidentNote) {
          return 1;
        }
        if (aHasResidentNote && bHasResidentNote) {
          const diff = compareStrings(
            a.notes?.residentNote,
            b.notes?.residentNote,
            this.sortOrder
          );
          // Secondary sort on index
          return diff === 0 ? this.compareIndex(a, b) : diff;
        }

        // Neither have any note
        return this.compareIndex(a, b);
      }
    });
  }

  private getProperty(booking: EventBooking): string | undefined {
    switch (this.sortField) {
      case BookingColumnNames.Source:
        return booking.source;
      case BookingColumnNames.Resident:
        return booking.residentData?.name;
      case BookingColumnNames.Status:
        return booking.status;
      case BookingColumnNames.Paid:
        return booking.isPaidByFee
          ? "order3"
          : booking.isPaid
          ? "order2"
          : "order1";
    }
    return "";
  }
}
